import { createContext, type ReactNode, useContext } from "react";
import type { TenantEdgeUrlOptions } from "../../shared/edgeUrl";
import { tenantEdgeUrlOptionsForTenant } from "../lib/tenantOrigin";

type SessionTenantOrigins = {
  origin: string;
  edgeOrigin: string;
  edgeWildcardDomain: string | null;
  apiOrigin: string;
};

const SessionTenantOriginsContext = createContext<SessionTenantOrigins | null>(
  null
);

export function SessionTenantOriginProvider({
  origin,
  edgeOrigin,
  edgeWildcardDomain,
  apiOrigin,
  children,
}: {
  origin: string;
  edgeOrigin: string;
  edgeWildcardDomain: string | null;
  apiOrigin: string;
  children: ReactNode;
}) {
  return (
    <SessionTenantOriginsContext.Provider
      value={{ origin, edgeOrigin, edgeWildcardDomain, apiOrigin }}
    >
      {children}
    </SessionTenantOriginsContext.Provider>
  );
}

function useSessionTenantOrigins(): SessionTenantOrigins {
  const origins = useContext(SessionTenantOriginsContext);
  if (!origins) throw new Error("Session tenant origin is unavailable");
  return origins;
}

export function useSessionTenantOrigin(): string {
  return useSessionTenantOrigins().origin;
}

export function useSessionTenantEdgeOrigin(): string {
  return useSessionTenantOrigins().edgeOrigin;
}

export function useSessionTenantEdgeWildcardDomain(): string | null {
  return useSessionTenantOrigins().edgeWildcardDomain;
}

export function useSessionTenantEdgeUrlOptions(): TenantEdgeUrlOptions {
  const { edgeOrigin, edgeWildcardDomain } = useSessionTenantOrigins();
  return tenantEdgeUrlOptionsForTenant({
    edgeOrigin,
    edgeWildcardDomain,
  });
}

export function useSessionTenantApiOrigin(): string {
  return useSessionTenantOrigins().apiOrigin;
}
